import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useMemoizedFn } from 'ahooks';
import CardSlot from '@gp/components/CardSlot';
import GPCollapseStateSwitchBtn from '@gp/components/CollapseStateSwitchBtn';
import { getSecureMaxShowOfMeetingsPanels } from '../Meetings/utils';

import './index.scss';

const StudioManagementCollapsiblePanelList: React.FC<IStudioManagementCollapsiblePanelListProps> = (props) => {
  const { style, className, maxShow: maxShowProps, isShowAll: isShowAllProps, list } = props;
  // 是否折叠状态列表，每一个item有独立的是否折叠状态
  const [isCollapsedStates, setIsCollapsedStates] = useState<boolean[]>([]);
  // 是否全部展示会议记录
  const [isShowAll, setIsShowAll] = useState(false);

  // 处理后的maxShow（安全值，如果传入的maxShowProps值异常，则使用默认兜底的值）
  const maxShow = useMemo(() => getSecureMaxShowOfMeetingsPanels(maxShowProps), [maxShowProps]);

  // 是否展示 Show All、Show Less 切换按钮
  const showToggleBtn = useMemo(() => list?.length > maxShow, [maxShow, list]);

  const showList = useMemo(() => {
    if (!Array.isArray(list)) return [];
    return isShowAll ? list : list?.slice(0, maxShow);
  }, [isShowAll, maxShow, list]);

  const updateIsCollapsedStates = useMemoizedFn(
    (index: number, value: boolean, e: React.SyntheticEvent<HTMLSpanElement>) => {
      e?.stopPropagation();
      e?.preventDefault();
      if (!Array.isArray(isCollapsedStates)) return;
      // 同时只能展开一个会议记录，展开当前会议记录，其他已展开的会议记录需要折叠起来
      const newIsCollapsedStates = isCollapsedStates.map((item, itemIndex) => itemIndex !== index || value);
      setIsCollapsedStates(newIsCollapsedStates);
    },
  );

  const handleToggleShowAllOrLess = useMemoizedFn((e: React.SyntheticEvent<HTMLSpanElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsShowAll(!isShowAll);
  });

  useEffect(() => {
    if (typeof isShowAllProps !== 'boolean') return;
    if (isShowAllProps === isShowAll) return;
    setIsShowAll(isShowAllProps);
  }, [isShowAllProps]);

  useEffect(() => {
    if (!Array.isArray(list)) return;
    const isCollapsedStates = list.map((item) => !!item.isCollapsed);
    setIsCollapsedStates(isCollapsedStates);
  }, [list]);

  return (
    <div style={style} className={classnames('dbg-studio-management-collapsible-panel-list-comp', className)}>
      <div className="dbg-studio-management-collapsible-panel-list">
        {showList?.length > 0 &&
          showList?.map((item, index) => {
            const isCollapsed = !!isCollapsedStates?.[index];
            return (
              <CardSlot
                key={index}
                className={classnames('dbg-studio-management-collapsible-item', {
                  'is-collapsed': isCollapsed,
                })}
              >
                <div className="dbg-studio-management-collapsible-header">
                  {item?.headerRender(isCollapsed)}
                  <GPCollapseStateSwitchBtn
                    className="dbg-studio-management-collapsible-btn"
                    value={isCollapsed}
                    onChange={(value, e) => {
                      updateIsCollapsedStates(index, value, e);
                    }}
                  />
                </div>
                <div className="dbg-studio-management-collapsible-body">{item?.bodyRender?.(isCollapsed)}</div>
              </CardSlot>
            );
          })}
      </div>
      {showToggleBtn && (
        <footer className="dbg-studio-management-collapsible-panel-list-bottom-bar">
          <span className="dbg-studio-management-collapsible-panel-list-toggle-btn" onClick={handleToggleShowAllOrLess}>
            {isShowAll ? 'Show Less' : 'Show All'}
          </span>
        </footer>
      )}
    </div>
  );
};

export default React.memo(StudioManagementCollapsiblePanelList);
